import { Box, CircularProgress, Grid, GridProps } from '@material-ui/core';
import { formatDateTimeReadable, HppDistribution, HppDistributionChannel } from '@nemuru-eng/js-api-client';
import { ReactNode } from 'react';

import { useHppDistributionPolling } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Header/CheckoutSessionActions/components/DistributionHistoryDialog/hooks/useHppDistributionPoling';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';

export const DistributionChannelIcon = {
  [HppDistributionChannel.CHATTER]: 'chat',
  [HppDistributionChannel.MAILER]: 'email',
  [HppDistributionChannel.TEXTER]: 'sms',
};

interface DistributionCardProps {
  distribution: HppDistribution;
  hppId: string;
}

export const DistributionCard = ({ distribution, hppId }: DistributionCardProps) => {
  const { palette, customBorderRadius } = useCustomTheme();
  const { isMobile } = useScreen();
  const { pollingCounter, retryHppPolling } = useHppDistributionPolling({ hppId });

  const shortedCellClassName = isMobile ? 'shorted-cell' : '';

  // @andremaia - Cuando no se comparte el link (se hace copy paste), aparece como si se hubiese enviado por email
  // pero no se envía realmente, y aparece un mensaje de "An error ocurred" con un icono de warning.

  return (
    <Grid
      container
      className="overflow-text-ellipsis"
      style={{
        height: '52px',
        borderRadius: customBorderRadius.paper,
        border: `1px solid ${palette.grey[200]}`,
        marginBlock: '12px',
        alignItems: 'center',
      }}
    >
      {!isMobile && (
        <CardCell justifyContent="center" item xs={1}>
          <Box display={'flex'} height={'100%'} alignItems={'center'} justifyContent={'center'}>
            <Icon color={palette.secondary.main} size={20}>
              {DistributionChannelIcon[distribution.channel]}
            </Icon>
          </Box>
        </CardCell>
      )}
      <CardCell titleTid="SENT_TO" item xs={4}>
        <Box className={'overflow-text-ellipsis'}>
          <Font variant="body2" color="textSecondary" className={`overflow-text-ellipsis ${shortedCellClassName}`}>
            {distribution.recipient}
          </Font>
        </Box>
      </CardCell>
      <CardCell titleTid={isMobile ? 'SENT' : 'SENT_AT_TIME'} item xs={4}>
        <Font className="overflow-text-ellipsis" variant="body2" color="textSecondary">
          {pollingCounter === 0 && !distribution.sentAt && (
            <Box display={'flex'} alignItems={'center'}>
              <Font variant="body2" style={{ marginRight: '6px' }}>
                <Text tId={'AN_ERROR_OCURRED'} />
              </Font>
              <Icon size={16} color={palette.warning.main}>
                warning
              </Icon>
              <Icon
                size={16}
                color={palette.text.secondary}
                onClick={() => retryHppPolling()}
                style={{ cursor: 'pointer', paddingLeft: '6px' }}
              >
                send
              </Icon>
            </Box>
          )}
          {distribution.sentAt ? (
            <Box display={'flex'} alignItems={'center'}>
              <Box className={`overflow-text-ellipsis ${shortedCellClassName}`}>
                {formatDateTimeReadable(distribution.sentAt)}
              </Box>
              <Icon size={16} color={palette.success.main} style={{ paddingLeft: '4px' }}>
                check
              </Icon>
            </Box>
          ) : pollingCounter !== 0 ? (
            <Box display={'flex'} alignItems={'center'}>
              <Font variant="body2" className="overflow-text-ellipsis" style={{ marginRight: '6px' }}>
                <Text tId={'SENDING'} />
              </Font>
              <CircularProgress color="secondary" size={12} />
            </Box>
          ) : null}
        </Font>
      </CardCell>
      <CardCell titleTid={'VIEWED_AT'} item xs={4} sm={3}>
        <Box display={'flex'} overflow={'hidden'} alignItems={'center'}>
          <Font className={`overflow-text-ellipsis ${shortedCellClassName}`} variant="body2" color="textSecondary">
            {distribution.viewedAt ? formatDateTimeReadable(distribution.viewedAt) : <Text tId={'PENDING'} />}
          </Font>
          <Icon
            size={16}
            color={distribution.viewedAt ? palette.success.main : palette.grey[400]}
            style={{ paddingLeft: '4px' }}
          >
            {distribution.viewedAt ? 'check' : 'hourglass_empty'}
          </Icon>
        </Box>
      </CardCell>
    </Grid>
  );
};

interface CardCellProps extends GridProps {
  children: JSX.Element | ReactNode | string;
  titleTid?: string;
}

export const CardCell = ({ titleTid, children, ...props }: CardCellProps) => {
  return (
    <Grid {...props}>
      <Box px={2} py={1} display="flex" flexDirection={'column'}>
        {titleTid && (
          <Font variant="caption" color="textPrimary">
            <Text strong tId={titleTid} />:
          </Font>
        )}
        <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
          {children}
        </Box>
      </Box>
    </Grid>
  );
};
